/**
 * strings.en.js — English UI strings (the fallback for every language).
 * Keys are dotted ("today.addUsual"); `{name}` placeholders are filled by t(key, vars).
 */
const EN = {
  'app.title': 'Daily Operations',
  'app.howToUse': 'How to use',
  'app.accessRestricted': 'Access restricted to staff members',
  'tab.today': '📝 Today', 'tab.closing': '📋 Closing', 'tab.shopping': '🛒 Shopping', 'tab.backup': '📦 Backup',
  'tab.cutting': '🔪 Cutting', 'tab.review': '🔎 Review', 'tab.history': '🕓 History',
  'lang.label': 'Language',

  // Help / how-to modal
  'help.title': '❓ How to use Daily Operations',
  'help.aria': 'How to use Daily Operations',
  'help.intro': 'Note what the restaurant needs for tomorrow — what to buy, cut, cook & clean, plus any issues. Everyone sees it live.',
  'help.sec.today.title': 'Today',
  'help.sec.today.body': 'Note what’s needed for tomorrow in each list — type it, tap a “usually needed” item, or ✨ Add my usual. Tick items off as they’re handled. The board is ONE open list: it stays until someone taps 📦 Move list to History. From closing time (8 PM) the first new item asks whether to start a new list or keep adding to the open one.',
  'help.sec.shopping.title': 'Shopping',
  'help.sec.shopping.body': 'Your standing shopping list, grouped by store. Pick items from the checklist on the right (or type a custom one); set the 🏪 store (remembered + auto-filled next time). As you shop, tap the ○ (or the name) to tick an item off — it’s logged with today’s date and leaves the list (Undo in the toast); ✓ Bought all logs the whole list in one tap. ✕ on a checklist item deletes it from the checklist — its history stays, and adding it again brings it back.',
  'help.sec.cutting.title': 'Cutting',
  'help.sec.cutting.body': 'The standing prep-cutting list. Pick the items to cut, then tap ✓ Cut with the date it was done.',
  'help.sec.review.title': 'Review',
  'help.sec.review.body': 'Anything marked Not found or Out of stock while shopping lands here for a manager to source. Select items and Refill all once they’re sorted, or tap 🛒 to add them straight to the Shopping list to buy next run.',
  'help.sec.summary.title': 'Summary (inside each list)',
  'help.sec.summary.body': 'Tap 🕓 History on Shopping, Backup or Cutting and you land on 📊 Summary — one row per item showing how many times you ordered it, its rhythm on a timeline, the typical gap between orders, the last quantity and the stores used. Pick a range or exact from/to dates. Because it learns how often you actually buy each item, it puts a “Due to reorder” strip at the top — tap any of those to add it straight back to the list. Switch to 🗒 Every order for the raw log.',
  'help.sec.history.title': 'History',
  'help.sec.history.body': 'Every closed list, day by day: how many items, how many got done, what wasn’t, what went to Review, and who closed it — tap a day to see its items. Pick a range or exact from/to dates (older than 60 days loads on demand), filter by list or status, switch to 📋 All items, or export a CSV.',
  'help.sec.lists.title': 'Your lists',
  'help.sec.lists.body': '🧑‍🍳 Chef Groceries holds the chef’s pantry — dals, flours & rava, spices & masalas, chillies, curry leaves, ginger & garlic, herbs, rice, oils, salt, tamarind & jaggery. Type an item into any list’s search and it lands in the list it belongs to. Tap ✏️ Edit lists (above the lists) to add a new list — e.g. Bakery or Bar — reorder them, or delete one you made; ✏️ Edit on a list (or its ⚙) renames it, changes its icon or colour, moves items to another list (they’re suggested there from then on) or removes items from it (no longer suggested there; noting one again brings it back).',
  'help.sec.closing.title': 'Closing',
  'help.sec.closing.body': 'Walk the stations at closing and tap what’s low — it goes onto the open list, in the list it belongs to (tap again to take it off). ✏️ Edit changes the sheet: add, rename, reorder or delete sections, sub-sections and items, and pick which list each one feeds; ✨ Sort into lists checks every item’s list against its name. Tick several items (or a whole section) to move them to another sub-section or delete them at once.',
  'help.sec.backup.title': 'Backup',
  'help.sec.backup.body': 'The reserve stock to keep topped up — frozen breads, snacks, spare to-go boxes. Works like Shopping without stores: pick what’s running low, tick each off when it’s restocked.',
  'help.goodToKnow': '💡 Good to know',
  'help.tip.1': 'Nothing to submit — every change saves and syncs live across all devices.',
  'help.tip.2': '🎤 Speak instead of typing — tap a Search box, then the microphone on the iPad keyboard, and say the item (e.g. “2 crates onions”).',
  'help.tip.3': 'Nothing disappears on its own — the list stays on Today until it’s moved to History (who closed it and when is recorded). When you move it you can keep unfinished items on the new list. 👁 Show all reveals items already ticked off.',
  'help.tip.4': 'On touch (iPad or phone): tap a row to complete it, swipe left to delete, or tap ⋯ for more actions (status, ⭐ urgent, move, delete).',
  'help.tip.5': 'On Tuesdays and Fridays a 📆 banner reminds you to work the Shopping list.',
  'help.gotIt': 'Got it',

  // Category names (by id)
  'cat.groceries': 'Groceries & Ingredients', 'catShort.groceries': 'Groceries',
  'cat.supplies': 'To-Go Supplies', 'catShort.supplies': 'To-Go',
  'cat.prep': 'Prep List', 'catShort.prep': 'Prep',
  'cat.cooking': 'Cooking List', 'catShort.cooking': 'Cooking',
  'cat.cleaning': 'Cleaning & Closing', 'catShort.cleaning': 'Cleaning',
  'cat.issues': 'Issues & Notes', 'catShort.issues': 'Issues',
  'cat.chef': 'Chef Groceries', 'catShort.chef': 'Chef',

  // Statuses
  'status.needed': 'Needed', 'status.refilled': 'Refilled',
  'status.not_found': 'Not found', 'status.oos': 'Out of stock',

  // Today panel
  'today.usuallyNeeded': '✨ Usually needed',
  'today.addUsual': '✨ Add my usual ({n})',
  'today.searchAdd': '🔍 Search or add to {list}…',
  'today.addQty': '＋ Qty',
  'today.nothingYet': 'Nothing added yet.',
  'today.allClear': '✅ All {verb} — nothing outstanding.',
  'today.moveToHistory': 'Move to history →',
  'today.doneCount': '✓ {n} {verb}',
  'today.addRow': 'Add “{q}”',
  'today.nothingFound': 'Nothing found — keep typing to add it.',
  'today.typeToAdd': 'Type above to add an item.',
  'today.onList': 'on the list',
  'today.searchGeneric': '🔍 Search or add an item…',
  'today.searchAria': 'Search or add an item',
  'today.commonItem': 'Common item',
  'today.neededRecent': 'Needed on {n} recent day(s)',
  'today.alreadyOnList': 'Already on the list',
  'today.moveToHistoryHint': 'Clear these off the board — they stay in History',
  'today.movedToHistory': 'Moved {n} to history',
  'today.progressLabel': 'of {total} done',
  'today.toGo': ' · {n} to go',
  'today.allDone': ' · all done ✓',
  'today.shoppingList': '🛒 Shopping list',
  'today.shoppingListHint': 'Printable / shareable shopping list',
  'today.previewHint': 'Preview all items — screenshot, print, or share',
  'today.toSource': '⚠ {n} to source',
  'today.toSourceHint': 'Review items not found / out of stock',
  'today.showAll': '👁 Show all',
  'today.outstandingOnly': '🛒 Outstanding only',
  'today.nothingDoneYet': 'Nothing done yet',
  'today.hideDoneHint': 'Hide items already done',
  'today.savesSync': 'changes save & sync automatically',
  'today.otherLists': 'In other lists',
  'today.addToList': 'Add to {list}',
  'today.addedTo': '{name} → {list}',
  'today.alreadyIn': '{name} is already on {list}',

  // Done verbs (per list)
  'verb.refilled': 'refilled', 'verb.prepped': 'prepped', 'verb.cooked': 'cooked',
  'verb.cleaned': 'cleaned', 'verb.resolved': 'resolved', 'verb.done': 'done',

  // Reminders
  'reminder.shoppingDayTitle': 'Shopping day',
  'reminder.shoppingDayBody': "— work the Shopping list & cross-check what's low for your {day} run.",
  'reminder.dayTuesday': 'Tuesday', 'reminder.dayFriday': 'Friday',
  'reminder.openShopping': '🛒 Open Shopping list',
  'reminder.dismissShoppingDay': 'Dismiss shopping-day reminder',
  'reminder.endOfDay': "End of day — jot down what we need for tomorrow so nothing's missed in the morning.",
  'reminder.dismiss': 'Dismiss reminder',
  'reminder.voiceTitle': 'Faster than typing:',
  'reminder.voiceBody': ' tap a search box, then the microphone on the keyboard, and say the item — e.g. “2 crates onions”.',
  'reminder.dismissVoice': 'Dismiss voice tip',

  // Preview / shopping sheet
  'preview.shoppingTitle': '🛒 Shopping list',
  'preview.shoppingSubtitle': 'List for {date} · {n} to buy',
  'preview.dailyTitle': '🧺 Daily Operations',
  'preview.dailySubtitle': 'List for {date} · {n} still needed',
  'preview.copied': 'Copied to clipboard',
  'preview.tgSent': 'Sent to Telegram ✓', 'preview.tgFailed': 'Telegram send failed',
  'preview.nothingToShow': 'Nothing to show yet.',
  'preview.zoomGroup': 'Zoom preview', 'preview.zoomOut': 'Zoom out', 'preview.zoomIn': 'Zoom in', 'preview.zoomReset': 'Reset to 100%',
  'preview.pickLabel': 'Choose lists to share',
  'preview.sharingAll': 'Sharing all lists · tap a list to share only that one',
  'preview.sharingSome': 'Sharing {n} of {total} lists · tap again to remove',
  'preview.copy': '⧉ Copy', 'preview.print': '🖨 Print',
  'preview.whatsapp': '💬 WhatsApp', 'preview.whatsappTitle': 'Open WhatsApp with this list — pick your group or a contact, then send',
  'preview.tgSending': '⏳ Sending…', 'preview.tgDone': '✓ Sent', 'preview.tgSend': '📨 Send to Telegram',

  // Row actions menu
  'menu.actions': 'Item actions', 'menu.delete': 'Delete',
  'menu.markUrgent': 'Mark urgent', 'menu.removeUrgent': 'Remove urgent',
  'menu.moveToList': 'Move to list', 'menu.rename': 'Rename',
  'menu.remove': 'Remove {name}', 'menu.more': 'More actions',
  'menu.fixTelugu': 'Fix Telugu name', 'menu.fixTeluguPrompt': 'Telugu name for “{name}”:',

  // Item row / quantity
  'item.tapToEdit': 'Tap to edit',
  'item.editName': 'Edit {name}',
  'item.urgent': 'Urgent',
  'item.noted': 'Noted {date}',
  'item.undoNeeded': 'Undo — mark still needed',
  'item.markNeeded': 'Mark still needed',
  'qty.placeholder': 'e.g. 2 crates',
  'qty.setAria': 'Set quantity',
  'qty.addTitle': 'Add a quantity',
  'qty.addAria': 'Add quantity',
  'qty.editTitle': 'Quantity: {qty} — tap to edit',
  'qty.editAria': 'Quantity {qty}, tap to edit',

  // Handle verbs (past / imperative) for non-sourceable lists + refill
  'act.mark.refilled': 'Mark refilled',
  'act.done.prep': 'Prepped', 'act.mark.prep': 'Mark prepped',
  'act.done.cooking': 'Cooked', 'act.mark.cooking': 'Mark cooked',
  'act.done.cleaning': 'Cleaned', 'act.mark.cleaning': 'Mark cleaned',
  'act.done.issues': 'Resolved', 'act.mark.issues': 'Mark resolved',
  'act.done.task': 'Done', 'act.mark.task': 'Mark done',

  // Standing checklists (Shopping / Backup / Cutting)
  'checklist.label.shopping': 'Shopping List', 'checklist.verb.shopping': 'Bought',
  'checklist.label.backup': 'Backup Stock', 'checklist.verb.backup': 'Refilled',
  'checklist.label.cutting': 'Cutting', 'checklist.verb.cutting': 'Cut',
  'checklist.cadence.shopping': 'Tue · Fri',
  'checklist.intro.shopping': 'Par-check list — tap items to add, set the store, and tick them off with the date you bought them.',
  'checklist.intro.backup': 'Backup / reserve stock to keep topped up — frozen breads, snacks and spare to-go boxes. Tick each off with the date you restocked it, so nothing runs out mid-service.',
  'checklist.intro.cutting': 'Prep-cutting list — tick each item off with the date it was cut.',
  'checklist.markLog': 'Mark {name} {verb} & log',
  'checklist.markLogTitle': 'Mark {verb} — logs it to history and clears it',
  'checklist.addQty': '＋ qty', 'checklist.qtyFor': 'Quantity for {name}',
  'checklist.editStore': 'Edit store for {name}', 'checklist.storeSet': 'Store: {store}',
  'checklist.setStore': 'Set store', 'checklist.whichStore': '🏪 which store?',
  'checklist.storeFor': 'Store for {name}',
  'checklist.removeItem': 'Remove {name}', 'checklist.removeNoLog': 'Remove from list (without logging)',
  'checklist.previewStore': 'Preview, print, or share — grouped by store',
  'checklist.preview': 'Preview, print, or share',
  'checklist.backToList': '← Back to list', 'checklist.history': '🕓 History ({n})',
  'checklist.viewSummary': '📊 Summary', 'checklist.viewLog': '🗒 Every order ({n})',
  'checklist.filterAll': 'All ({n})', 'checklist.pendingWithCount': '○ Pending ({n})', 'checklist.pending': '○ Pending',
  'checklist.completedPill': '✓ {verb}',
  'checklist.csvTitle': 'Download this history as CSV',
  'checklist.noPending': 'No pending items', 'checklist.nothingMarked': 'Nothing marked {verb} yet', 'checklist.noHistory': 'No history yet',
  'checklist.toBuy': 'To buy', 'checklist.toCut': 'To cut',
  'checklist.paneChecklist': '📋 Checklist',
  'checklist.searchAdd': '🔍 Search or add…', 'checklist.searchAria': 'Search or add an item',
  'checklist.tapToAdd': 'Tap to add — or type a new one.',
  'checklist.removeFromList': 'Remove from list', 'checklist.addToList': 'Add to list',
  'checklist.deleteFromChecklist': 'Delete {name} from checklist', 'checklist.removeFromChecklist': 'Remove from checklist',
  'checklist.noMatches': 'No matches.',
  'checklist.completeAll': '✓ {verb} all ({n})', 'checklist.completeAllTitle': 'Log all {n} as {verb} and clear the list',
  'checklist.emptyActiveTitle': 'Nothing on the list yet.', 'checklist.emptyActiveHint': 'Tap items from the checklist, or type a custom one above.',
  'checklist.addItemsTo': 'Add items to {store}', 'checklist.addItemsPlaceholder': 'Add items — milk, eggs, bread…',
  'checklist.notifyDone': '{verb} “{name}” ✓', 'checklist.notifyAll': '{verb} all {n} ✓',
  'checklist.notifyRemoved': 'Removed “{name}” from checklist',
  'checklist.logRemoved': 'Deleted {name} ({date}) from history',

  // Audit-table columns (History + checklist history)
  'col.item': 'Item', 'col.added': 'Added', 'col.completed': 'Completed', 'col.status': 'Status',
  'col.by': 'By', 'col.store': 'Store', 'col.list': 'List', 'col.qty': 'Qty', 'col.addedBy': 'Added by', 'col.handledBy': 'Handled by',
  'table.sortBy': 'Sort by {label}', 'table.actions': 'Actions', 'table.remove': 'Remove {name}', 'table.entry': 'entry',
  'table.details': 'Show every order',

  // Summary view — ordering history rolled up per item (inside each checklist tab)
  'audit.allTime': 'All time', 'audit.lastDays': 'last {n} days', 'audit.anyStart': 'the beginning',
  'audit.fromAria': 'From date', 'audit.toAria': 'To date', 'audit.clearDates': 'Clear custom dates',
  'audit.csvTitle': 'Download this summary as CSV',
  'audit.colTimes': 'Times', 'audit.colQty': 'Qty', 'audit.colLast': 'Last',
  'audit.colEvery': 'Every', 'audit.colRhythm': 'Rhythm',
  'audit.everyDays': '~{n} d', 'audit.daysAgo': '({n} d ago)', 'audit.today': '(today)',
  'audit.rhythmLabel': '{n} orders between {from} and {to}',
  'audit.kpiOrders': 'orders logged', 'audit.kpiItems': 'distinct items',
  'audit.kpiDays': 'ordering days', 'audit.kpiDue': 'due to reorder',
  'audit.showing': 'Showing {range}', 'audit.byStore': 'Orders by store',
  'audit.everyOrder': 'Every order ({n})', 'audit.addedOn': 'added {date}',
  'audit.dueTitle': 'Due to reorder', 'audit.dueSub': 'Based on how often you usually buy these — tap to add.',
  'audit.dueMeta': 'every ~{every} d · {since} d ago', 'audit.dueAdd': 'Add {name} back to the list',
  'audit.dueNow': 'Due to reorder', 'audit.pendingTitle': '{n} added but never completed',
  'audit.notifyReordered': 'Added “{name}” to the list',
  'audit.noneYet': 'Nothing logged yet',
  'audit.noneHint': 'Once you tick items off as {verb}, this builds a picture of how often you order each one.',
  'audit.tryWider': 'Try a wider date range or a different search.',

  // Review tab
  'review.emptyTitle': 'Nothing to follow up',
  'review.emptyBody': 'When staff flag an item {notFound} or {oos} while refilling, it lands here to source and clear.',
  'review.backToToday': '← Back to today',
  'review.intro': "Items staff couldn't refill. Double-check stock, then mark refilled — or add them to the Shopping list to buy next run. Works one at a time, several selected, or a whole group.",
  'review.selected': '{n} selected',
  'review.markRefilled': '✓ Mark refilled', 'review.addToShopping': '🛒 Add to shopping',
  'review.selectAll': 'Select all {label}', 'review.refillAll': '✓ Refill all',
  'review.shop': '🛒 Shop', 'review.shopTitle': 'Add to the Shopping list', 'review.refilled': '✓ Refilled',
  'review.by': 'by {name}', 'review.nItems': '{n} items',
  'review.notifyRefilled': '{n} marked refilled ✓', 'review.notifyAdded': '{label} added to Shopping list 🛒',

  // History tab
  'history.days': '{n} days',
  'history.searchItems': '🔍 Search items…', 'history.searchAria': 'Search history',
  'history.filterAria': 'Filter by list', 'history.allLists': 'All lists',
  'history.csvTitle': 'Download this range as CSV', 'history.itemsNoted': 'items noted', 'history.mostNeeded': 'Most needed',
  'history.trackSpend': '💵 Track spend in Expense Calculator →',
  'history.loading': 'Loading history…',
  // Open list (Today board) — see ListDialog / DailyOps root
  'list.forDay': '📋 List for {date}', 'list.since': 'started {date}',
  'list.moveBtn': '📦 Move list to History', 'list.moveHint': 'Close this list — everything is kept in History with who did what',
  'list.staleTitle': 'This list is for {date}.', 'list.staleBody': 'It stays until someone moves it to History — do that when tonight’s closing starts a new list (or keep adding to it).',
  'list.dlgNewTitle': '🌙 Start the list for {date}?', 'list.dlgMoveTitle': '📦 Move this list to History?',
  'list.dlgOpen': 'The open list is for {date} · {n} items:',
  'list.statDone': '✓ {n} done', 'list.statNotDone': '○ {n} not done', 'list.statFlagged': '⚠ {n} to source',
  'list.dlgAdding': 'You’re adding “{name}”.',
  'list.startNew': '🆕 Move it to History & start a new list', 'list.startNewSub': 'Saved in History with who noted, did and closed what.',
  'list.keepRecent': 'Keep the {n} unfinished item(s) noted today on the new list',
  'list.keepOlder': 'Keep the {n} unfinished item(s) from earlier on the new list',
  'list.sameList': '➕ Keep adding to the same list', 'list.sameListSub': 'Nothing moves — the new item joins the open list.',
  'list.moveConfirm': 'Move {n} to History', 'list.moved': 'Moved {n} item(s) to History',
  'list.movedKept': 'Moved {n} to History · kept {k} on the new list', 'list.addingN': '{n} usual items',
  'item.movedTo': 'Moved to {list}', 'item.removed': 'Removed “{name}”',
  'col.listFor': 'List for', 'col.closed': 'Closed', 'col.items': 'Items', 'col.done': '✓ Done',
  'col.notDone': '○ Not done', 'col.flagged': '⚠ To source', 'col.completion': 'Done %',
  'history.notDone': 'Not done', 'history.lastDays': 'last {n} days', 'history.showing': 'Showing lists for {range}',
  'history.fromAria': 'From date', 'history.toAria': 'To date', 'history.clearDates': 'Clear dates',
  'history.statusAria': 'Filter by status', 'history.status.all': 'All', 'history.status.done': '✓ Done',
  'history.status.notDone': '○ Not done', 'history.status.flagged': '⚠ To source',
  'history.viewByDay': '📅 By day', 'history.viewItems': '📋 All items ({n})',
  'history.kpiLists': 'lists', 'history.kpiDone': 'done ({n} items)', 'history.kpiNotDone': 'not done',
  'history.loadingOlder': 'Loading older history…', 'history.notedBy': 'Noted by {name}', 'history.showItems': 'Show this list’s items',
  'history.olderFailed': 'Couldn’t load the days before {date} — they need a connection. Try again in a moment.',
  'history.noMatching': 'No matching items', 'history.noHistoryRange': 'No history yet in this range',
  'history.tryDifferent': 'Try a different search or list.',
  'history.appearTomorrow': 'Lists show up here once they’re moved to History from the Today tab.',

  // Closing walk-through
  'closing.intro': "Walk the stations at closing — tap what's low and it's added to tomorrow's board.",
  'closing.newCycleHint': 'Fresh sheet for tonight — your first tap asks whether to move the open list to History and start a new one.',
  'closing.edit': '✏️ Edit', 'closing.doneEditing': '✓ Done',
  'closing.added': 'Added {name} → {cat}', 'closing.removed': 'Removed {name}',
  'closing.tapAdd': 'Tap to add to {cat}', 'closing.tapRemove': 'On {cat} — tap to remove',
  'closing.addItem': '＋ Add item', 'closing.addSection': '＋ Add section',
  'closing.newSection': 'New section', 'closing.sectionName': 'Section name', 'closing.itemName': 'Item name',
  'closing.sectionTarget': 'Section adds to', 'closing.itemTarget': 'Item adds to',
  'closing.moveUp': 'Move up', 'closing.moveDown': 'Move down',
  'closing.deleteItem': 'Delete item', 'closing.deleteSection': 'Delete section',
  'closing.deleteSectionConfirm': 'Delete section “{title}” and all its items?',
  'closing.sectionEmpty': 'No items yet.', 'closing.empty': 'No checklist yet.',
  'closing.loadDefault': '📋 Load our closing sheets',
  'closing.newSubsection': 'New sub-section', 'closing.subsectionName': 'Sub-section name',
  'closing.subsectionTarget': 'Sub-section adds to', 'closing.deleteSubsection': 'Delete sub-section',
  'closing.deleteSubConfirm': 'Delete sub-section “{title}” and its items?', 'closing.addSubsection': '＋ Add sub-section',
  'closing.emptyHint': 'This checklist is empty.', 'closing.reset': '↺ Reset to our sheets',
  'closing.resetConfirm': 'Replace the entire closing checklist with the default sheets?',
  'closing.selectItem': 'Select {name}', 'closing.selectAllIn': 'Select all items in {title}',
  'closing.selectedN': '{n} selected', 'closing.clearSelection': 'Cancel', 'closing.deleteSelected': 'Delete {n}',
  'closing.deleteSelectedConfirm': 'Delete {n} selected item(s)?', 'closing.deletedN': 'Deleted {n} item(s)',
  'closing.moveTo': '↪ Move to…',
  'closing.movedN': 'Moved {n} item(s) to {title}',
  'sort.button': '✨ Sort into lists', 'sort.buttonHint': 'Check which list each item goes to, based on its name',
  'sort.title': '✨ Sort into the right lists',
  'sort.subtitle': 'Suggested from each item’s name. Untick anything that’s wrong, or pick a different list — nothing changes until you apply.',
  'sort.suggested': 'Suggested', 'sort.all': 'All items', 'sort.search': 'Search items…',
  'sort.allGood': 'Every item is already in the right list.', 'sort.noMatch': 'No items match.',
  'sort.because': '“{word}” → {cat}', 'sort.wasIn': 'Currently in', 'sort.includeItem': 'Move {name}',
  'sort.listFor': 'List for {name}', 'sort.noChanges': 'No changes selected',
  'sort.boardToo': 'Items already on today’s board move too.',
  'sort.apply': 'Apply {n} changes', 'sort.applied': 'Moved {n} item(s) to their lists',
  'sort.autoPlaced': '{name} → {cat} (from “{word}”)',
  'sort.staysHint': 'Stays in {cat} — won’t be suggested again', 'sort.keptHint': 'You chose this list — suggestions leave it alone',
  'sort.keepingN': 'Keeping {n} as is', 'sort.save': 'Save', 'sort.keptN': 'Kept {n} item(s) where they are',
  'sort.boardMoved': '{n} item(s) on today’s board moved too',

  // Lists — make your own, restyle / reorder any, move items between them
  'lists.new': 'New list', 'lists.newHint': 'Make your own — e.g. Bakery, Bar, Desserts',
  'lists.settingsFor': 'List settings — {name}',
  'lists.createTitle': 'New list', 'lists.editTitle': 'Edit “{name}”',
  'lists.builtinNote': 'Built-in list — rename it, change its icon or colour, move it, or move items in and out.',
  'lists.name': 'Name', 'lists.namePh': 'e.g. Bakery & Desserts', 'lists.icon': 'Icon', 'lists.color': 'Colour',
  'lists.kind': 'What goes on it?',
  'lists.kindBuy': '🛒 Things to buy', 'lists.kindBuySub': 'Refilled · Not found · Out of stock — joins the 🛒 Shopping list',
  'lists.kindTask': '✅ Tasks to do', 'lists.kindTaskSub': 'Just done / not done — like Cleaning',
  'lists.qty': 'Items can have a quantity (e.g. “2 crates”)',
  'lists.position': 'Position {n} of {total}', 'lists.moveEarlier': '▲ Earlier', 'lists.moveLater': '▼ Later',
  'lists.items': 'Items', 'lists.showFrom': 'Show items from',
  'lists.itemsHint': 'Tick items, then move them to another list or remove them from this one. Moved items are suggested in their new list from then on (any on today’s list move too); removed ones stop being suggested here.',
  'lists.searchItems': 'Search items…', 'lists.selectAll': 'Select all ({n})', 'lists.selectedN': '{n} selected',
  'lists.moveTo': 'Move to', 'lists.moveSelected': 'Move {n}',
  'lists.removeSelected': 'Remove {n}',
  'lists.removeConfirm': 'Remove {n} item(s) from {list}? They won’t be suggested there any more. Today’s list and History don’t change, and noting one again brings it back.',
  'lists.removed': 'Removed {n} item(s) from {list}',
  'lists.noItems': 'No items yet — they show up here as people add them.',
  'lists.create': 'Create list', 'lists.save': 'Save', 'lists.delete': '🗑 Delete this list',
  'lists.deleteConfirm': 'Delete “{name}”?{extra}', 'lists.deleteExtra': ' Its {n} item(s) on the open list move to History.',
  'lists.reset': '↺ Reset to default', 'lists.resetConfirm': 'Put “{name}” back to its default name, icon, colour and position?',
  'lists.created': 'Created “{name}” ✓', 'lists.saved': 'Saved ✓', 'lists.deleted': 'Deleted “{name}”',
  'lists.moved': 'Moved {n} item(s) to {list}',
  'lists.nameRequired': 'Give the list a name.', 'lists.nameTaken': 'There’s already a list called “{name}”.',
  'lists.unknown': 'Other list',
  'lists.editMode': '✏️ Edit lists', 'lists.doneEditing': '✓ Done', 'lists.boardLabel': '🗂 {n} lists',
  'lists.addNew': 'Add a new list',
  'lists.manageHint': 'Tap ✏️ Edit to rename a list, change its icon or colour, or move / remove its items · ▲▼ change the order · 🗑 deletes a list you made.',
  'lists.kindBuyShort': '🛒 To buy', 'lists.kindTaskShort': '✅ Tasks', 'lists.openN': '{n} on today’s list', 'lists.builtin': 'built-in',
  'lists.editBtn': '✏️ Edit', 'lists.editAria': 'Edit {name}',
  'lists.moveUpAria': 'Move {name} up', 'lists.moveDownAria': 'Move {name} down', 'lists.deleteAria': 'Delete {name}',
  'tidy.title': '{n} item(s) belong in another list.', 'tidy.example': 'e.g. {name} → {list}',
  'tidy.btn': '✨ Move them', 'tidy.done': 'Moved {n} item(s) to their lists',

  // Common
  'common.preview': '🖨 Preview', 'common.close': 'Close', 'common.undo': 'Undo',
  'common.csv': '⬇ CSV', 'common.done': 'Done', 'common.add': 'Add', 'common.cancel': 'Cancel',
  'common.loading': 'Loading…', 'common.clear': 'Clear', 'common.remove': 'Remove',
  'common.retry': 'Try again',
  'common.saveFailed': 'Couldn’t save that — please try again.',
  'common.noPermission': 'Couldn’t save — this account isn’t allowed to, or the database rules need updating (ask the owner).',
  'common.goneElsewhere': 'Couldn’t save — it was deleted on another device.',
  'error.quota': 'Today’s database limit is used up, so this screen can’t refresh until it resets at {time}. It reconnects by itself; changes made here are kept and sync then.',
  'error.noAccess': 'This account can’t open Daily Ops data — ask the owner to check its role.',
  'error.load': 'Couldn’t load the latest data — showing what this device has and retrying. ({msg})',
};

export default EN;
